/**
 * Link validation for the host-styled rich-text control (RichTextControl.tsx).
 *
 * Mirrors the backend allowlist in site_components/html_sanitizer.py so the editor never offers a
 * link the server would silently drop: http / https / mailto / tel, or a relative URL
 * ("/terms", "#section", "page.html", "../x"). javascript:/vbscript:/data:, any other scheme, and
 * protocol-relative ("//host", "\\host", "/\\host") are rejected.
 * specs/completed/imported-site-deep-insert-zones.md Phase 3.
 */

const ALLOWED_SCHEMES = ['http', 'https', 'mailto', 'tel'];
// Same control/whitespace class the backend strips before its prefix check ([\x00-\x20\x7f]).
// eslint-disable-next-line no-control-regex
const CTRL_WS = /[\x00-\x20\x7f]+/g;
const SCHEME = /^([a-z][a-z0-9+.-]*):/i;

export type LinkCheck = { ok: true; href: string } | { ok: false; error: string };

/** Normalise and validate a user-entered link. Returns the href to store, or a message to show. */
export function checkLinkHref(raw: string): LinkCheck {
  let href = (raw || '').trim();
  if (!href) return { ok: false, error: 'Enter a link address.' };
  // A bare "www.example.com" is almost always meant as an external site, not a relative path.
  if (/^www\./i.test(href)) href = `https://${href}`;
  const squashed = href.replace(CTRL_WS, '');
  if (squashed.startsWith('//') || squashed.startsWith('\\') || squashed.startsWith('/\\')) {
    return { ok: false, error: 'Start the link with https:// (or / for a page on this site).' };
  }
  const m = squashed.match(SCHEME);
  if (m) {
    const scheme = m[1].toLowerCase();
    if (!ALLOWED_SCHEMES.includes(scheme)) {
      return { ok: false, error: 'Only http, https, mailto and tel links are allowed.' };
    }
    if (scheme === 'http' || scheme === 'https') {
      try {
        const u = new URL(href);
        if (!u.hostname) throw new Error('no host');
      } catch {
        return { ok: false, error: 'That web address is not valid.' };
      }
    } else if (squashed.length <= scheme.length + 1) {
      return { ok: false, error: `Add the ${scheme === 'tel' ? 'phone number' : 'email address'} after "${scheme}:".` };
    }
    return { ok: true, href };
  }
  if (/\s/.test(href)) return { ok: false, error: 'A link address cannot contain spaces.' };
  return { ok: true, href };
}

/** Boolean form used as TipTap Link's isAllowedUri (also guards pasted / autolinked hrefs). */
export const isSafeLinkHref = (href: string | undefined | null): boolean =>
  !!href && checkLinkHref(href).ok;
